import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Table,
  Group,
  Badge,
  ActionIcon,
  Stack,
  Alert,
  Paper,
  Button,
  Progress,
  Modal,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconRefresh,
  IconPlayerStop,
  IconDownload,
  IconUpload,
  IconClock,
} from '@tabler/icons-react';
import {
  useFogTasks,
  useCancelFogTask,
  getTaskStateLabel,
  getTaskStateColor,
  getTaskTypeName,
  isTaskActive,
  type FogImagingTask,
} from '@/hooks';
import { FogLoadingOverlay } from '@/components/fog';

export function FogTasksPage() {
  const { data, isLoading, error, refetch, isRefetching } = useFogTasks();
  const cancelMutation = useCancelFogTask();
  const [taskToCancel, setTaskToCancel] = useState<FogImagingTask | null>(null);
  const [cancelModalOpen, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false);
  const [pageVisible, setPageVisible] = useState(false);
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    setPageVisible(true);
  }, []);

  // Animate rows
  useEffect(() => {
    if (data?.tasks) {
      data.tasks.forEach((_, index) => {
        setTimeout(() => {
          setVisibleRows((prev) => new Set([...prev, index]));
        }, index * 50);
      });
    }
  }, [data?.tasks]);

  const tasks = data?.tasks || [];
  const activeTasks = tasks.filter(isTaskActive);

  const handleCancelClick = (task: FogImagingTask) => {
    setTaskToCancel(task);
    openCancelModal();
  };

  const handleConfirmCancel = async () => {
    if (!taskToCancel) return;

    try {
      await cancelMutation.mutateAsync(taskToCancel.id);
      closeCancelModal();
      setTaskToCancel(null);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const getTaskTypeIcon = (typeID: number) => {
    switch (typeID) {
      case 1: return <IconDownload size={16} />;
      case 2: return <IconUpload size={16} />;
      default: return <IconClock size={16} />;
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <FogLoadingOverlay message="Loading FOG Tasks..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load FOG tasks: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      size="xl"
      py="md"
      style={{
        opacity: pageVisible ? 1 : 0,
        transform: pageVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>FOG Tasks</Title>
          <Text c="dimmed" size="sm">
            {activeTasks.length} active task{activeTasks.length !== 1 ? 's' : ''}, {tasks.length} total
          </Text>
        </div>
        <Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={isRefetching}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Active Tasks Summary */}
      {activeTasks.length > 0 && (
        <Alert color="blue" mb="md" icon={<IconClock size={16} />}>
          <Group justify="space-between">
            <Text size="sm">
              {activeTasks.length} imaging task{activeTasks.length !== 1 ? 's' : ''} in progress
            </Text>
            <Text size="xs" c="dimmed">
              Auto-refreshing every 10 seconds
            </Text>
          </Group>
        </Alert>
      )}

      {/* Tasks Table */}
      <Paper withBorder radius="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Type</Table.Th>
              <Table.Th>Host</Table.Th>
              <Table.Th>Image</Table.Th>
              <Table.Th>Progress</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tasks.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" c="dimmed" py="xl">
                    No tasks found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              tasks.map((task, index) => (
                <Table.Tr
                  key={task.id}
                  style={{
                    opacity: visibleRows.has(index) ? 1 : 0,
                    transform: visibleRows.has(index) ? 'translateX(0)' : 'translateX(-10px)',
                    transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <Table.Td>
                    <Group gap="xs">
                      {getTaskTypeIcon(task.typeID)}
                      <Badge
                        variant="light"
                        color={task.typeID === 1 ? 'blue' : 'violet'}
                      >
                        {task.typeName || getTaskTypeName(task.typeID)}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>{task.hostName || `Host #${task.hostID}`}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{task.imageName || `Image #${task.imageID}` || '-'}</Text>
                  </Table.Td>
                  <Table.Td w={150}>
                    {task.percent !== undefined && task.percent >= 0 ? (
                      <Stack gap={4}>
                        <Progress
                          value={task.percent}
                          size="sm"
                          animated={isTaskActive(task)}
                          color={getTaskStateColor(task.stateID)}
                        />
                        <Text size="xs" c="dimmed" ta="center">
                          {task.percent}%
                        </Text>
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={getTaskStateColor(task.stateID)}>
                      {getTaskStateLabel(task.stateID)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {isTaskActive(task) && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleCancelClick(task)}
                        title="Cancel task"
                      >
                        <IconPlayerStop size={16} />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Cancel Confirmation Modal */}
      <Modal
        opened={cancelModalOpen}
        onClose={closeCancelModal}
        title="Cancel Task"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to cancel this imaging task?
          </Text>
          {taskToCancel && (
            <Paper withBorder p="sm" radius="md" bg="gray.0">
              <Stack gap={4}>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Type</Text>
                  <Badge size="sm" color={taskToCancel.typeID === 1 ? 'blue' : 'violet'}>
                    {getTaskTypeName(taskToCancel.typeID)}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Host</Text>
                  <Text size="sm">{taskToCancel.hostName || `Host #${taskToCancel.hostID}`}</Text>
                </Group>
                {taskToCancel.percent !== undefined && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Progress</Text>
                    <Text size="sm">{taskToCancel.percent}%</Text>
                  </Group>
                )}
              </Stack>
            </Paper>
          )}
          <Alert color="orange" variant="light">
            <Text size="sm">
              Cancelling a task may leave the host in an inconsistent state.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCancelModal}>
              Keep Running
            </Button>
            <Button
              color="red"
              onClick={handleConfirmCancel}
              loading={cancelMutation.isPending}
            >
              Cancel Task
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
